import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

/**
 * Необратимые операции просят подтверждения. Самая разрушительная — обезличивание
 * персональных данных: до этого она выполнялась одним кликом, и единственной
 * защитой было обязательное поле «Основание». Для таких действий мало «вы уверены»:
 * просим набрать слово, чтобы движение руки не совпадало с движением мысли.
 */
export interface ConfirmRequest {
  readonly title: string;
  readonly body?: ReactNode;
  /** Что именно произойдёт — списком, чтобы последствия читались, а не подразумевались. */
  readonly consequences?: readonly string[];
  readonly confirmLabel?: string;
  readonly cancelLabel?: string;
  readonly tone?: 'default' | 'danger';
  /** Если задано — кнопка разблокируется, только когда слово набрано точно. */
  readonly typeToConfirm?: string;
  /**
   * Если задано — диалог просит основание (не короче `reasonMin` символов) и
   * возвращает его. Раньше основание разделения склейки спрашивал нативный
   * `window.prompt`: без оформления, без проверки, мимо журнала (QA.md, Q25).
   */
  readonly reasonLabel?: string;
  readonly reasonMin?: number;
}

type Ask = (request: ConfirmRequest) => Promise<boolean>;
type AskReason = (request: ConfirmRequest & { readonly reasonLabel: string }) => Promise<string | null>;

interface ConfirmApi {
  readonly ask: Ask;
  readonly askReason: AskReason;
}

const ConfirmContext = createContext<ConfirmApi | undefined>(undefined);

export function useConfirm(): Ask {
  const api = useContext(ConfirmContext);
  if (!api) throw new Error('useConfirm вне ConfirmProvider');
  return api.ask;
}

/** Подтверждение с обязательным основанием: строка или null при отмене. */
export function useConfirmReason(): AskReason {
  const api = useContext(ConfirmContext);
  if (!api) throw new Error('useConfirmReason вне ConfirmProvider');
  return api.askReason;
}

interface Pending {
  readonly request: ConfirmRequest;
  readonly resolve: (value: string | null) => void;
}

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<Pending | undefined>(undefined);
  const [typed, setTyped] = useState('');
  const [reason, setReason] = useState('');

  const askReason = useCallback<AskReason>((request) => {
    setTyped('');
    setReason('');
    return new Promise<string | null>((resolve) => setPending({ request, resolve }));
  }, []);

  const ask = useCallback<Ask>(
    (request) =>
      new Promise<boolean>((resolve) => {
        setTyped('');
        setReason('');
        setPending({ request, resolve: (value) => resolve(value !== null) });
      }),
    [],
  );

  const close = useCallback(
    (confirmed: boolean) => {
      pending?.resolve(confirmed ? reason.trim() : null);
      setPending(undefined);
      setTyped('');
      setReason('');
    },
    [pending, reason],
  );

  useEffect(() => {
    if (!pending) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [pending, close]);

  const value = useMemo(() => ({ ask, askReason }), [ask, askReason]);
  const request = pending?.request;
  const danger = request?.tone === 'danger';
  const reasonReady =
    !request?.reasonLabel || reason.trim().length >= (request.reasonMin ?? 3);
  const ready =
    (!request?.typeToConfirm || typed.trim() === request.typeToConfirm) && reasonReady;

  return (
    <ConfirmContext.Provider value={value}>
      {children}
      {request ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-scrim p-4"
          style={{ animation: 'moika-overlay-in 120ms ease-out' }}
          role="dialog"
          aria-modal="true"
          aria-label={request.title}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) close(false);
          }}
        >
          <div
            className="w-[min(34rem,100%)] rounded-panel border border-line bg-surface p-5 shadow-sheet"
            style={{ animation: 'moika-pop-in 140ms ease-out' }}
          >
            <h2 className="text-base font-semibold text-ink">{request.title}</h2>
            {request.body ? (
              <div className="mt-2 text-sm text-ink-muted">{request.body}</div>
            ) : null}

            {request.consequences && request.consequences.length > 0 ? (
              <ul
                className={`mt-3 space-y-1 rounded-control border px-3 py-2 text-sm ${
                  danger ? 'border-danger-line bg-danger-bg text-danger' : 'border-line bg-sunken text-ink-muted'
                }`}
              >
                {request.consequences.map((item) => (
                  <li key={item} className="flex gap-2">
                    <span aria-hidden>—</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            ) : null}

            {request.typeToConfirm ? (
              <label className="mt-4 flex flex-col gap-1 text-sm">
                <span className="text-ink-muted">
                  Наберите{' '}
                  <span className="font-mono font-semibold text-ink">{request.typeToConfirm}</span>,
                  чтобы подтвердить
                </span>
                <input
                  autoFocus
                  value={typed}
                  onChange={(e) => setTyped(e.target.value)}
                  className="rounded-control border border-line-strong bg-surface px-2 py-1.5 font-mono text-sm text-ink outline-none focus:border-brand-500"
                />
              </label>
            ) : null}

            {request.reasonLabel ? (
              <label className="mt-4 flex flex-col gap-1 text-sm">
                <span className="text-ink-muted">{request.reasonLabel}</span>
                <input
                  autoFocus={!request.typeToConfirm}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  className="rounded-control border border-line-strong bg-surface px-2 py-1.5 text-sm text-ink outline-none focus:border-brand-500"
                />
              </label>
            ) : null}

            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => close(false)}
                className="rounded-control border border-line-strong px-3 py-1.5 text-sm font-medium text-ink transition hover:bg-sunken"
              >
                {request.cancelLabel ?? 'Отмена'}
              </button>
              <button
                type="button"
                autoFocus={!request.typeToConfirm && !request.reasonLabel}
                disabled={!ready}
                onClick={() => close(true)}
                className={`rounded-control px-3 py-1.5 text-sm font-medium text-ink-invert transition disabled:cursor-not-allowed disabled:opacity-40 ${
                  danger ? 'bg-danger hover:opacity-90' : 'bg-brand-600 hover:bg-brand-700'
                }`}
              >
                {request.confirmLabel ?? 'Подтвердить'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </ConfirmContext.Provider>
  );
}
